import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import SectionTitle from '../../shared/sectionTitle';
import NotificationCard from '../../shared/notificationCard';
import CustomButton from '../../shared/customButton';
import SelectField from '../../shared/selectField';
import EmployeeCycleAttributes from '../../shared/employeeCycleAttributes';
import EmployeeCycleAttributeRatings from '../../shared/employeeCycleAttributeRatings';
import EmployeeCycleReviewAnswersPreview from '../../shared/employeeCycleReviewAnswersPreview';
import { ReactComponent as SelfReviewIcon } from '../../../assets/icons/self-assesment-small_purple.svg';
import { ReactComponent as TopDownReviewIcon } from '../../../assets/icons/top_down_review_small.svg';
import { ReactComponent as DownUpReviewIcon } from '../../../assets/icons/down_up_review_small.svg';
import { sticky } from '../../../constants/helperCssRules';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { CYCLE_STEP_TYPES } from '../../../constants/cycle';
import { CYCLE_STATUSES } from '../../../constants/statuses';
import {
  api_user_review_cycles,
  api_user_review_cycle,
  api_user_review_cycle_open_ended_answers,
} from '../../../constants/apiRoutes';
import { useTranslations } from '../../../utility/useTranslations';
import { getItemById, isArrayEmpty } from '../../../utility/helpers';
import http from '../../../utility/http';
import {
  prepareCycleAttributes,
  prepareCycleAttributesForCompare,
  prepareEmployeeReviewAnswers,
} from '../../../utility/cycles';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    paddingBottom: spacing(4),
    ...sticky(primary.white, 170),
  },
  cycleSelect: {
    width: 438,
  },
  section: {
    display: 'flex',
    alignItems: 'center',
    margin: spacing(6, 0, 8, 0),
  },
  analyticsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    alignItems: 'start',
    gap: 24,
  },
  analyticsSectionTitle: {
    marginTop: spacing(7.75),
  },
  ratingsSection: {
    justifyContent: 'space-between',
  },
  actionsWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0,max-content))',
    gap: 4,
    alignItems: 'center',
  },
  actionButton: {
    color: primary.bluish4,
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish4}`,
    transition: 'border .3s ease, color .3s ease',
    '&:hover': {
      backgroundColor: primary.white,
      border: `1px solid ${primary.bluish4}`,
    },
  },
  selectedAction: {
    border: `1px solid ${primary.bluish1}`,
    color: primary.bluish1,
    '&:hover': {
      border: `1px solid ${primary.bluish1}`,
    },
  },
  actionIcon: {
    height: 16,
    width: 16,
    transition: 'fill 0.3s ease',
    '& path': {
      fill: primary.bluish4,
    },
  },
  actionIconSelected: {
    '& path': {
      fill: primary.bluish1,
    },
  },
}));

const PeopleReviewCyclesPage = ({ location, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_REVIEW_CYCLES);
  const { SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } = CYCLE_STEP_TYPES;

  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;
  const { state } = location;

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isAnswersPreviewOpened, setIsAnswersPreviewOpened] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [cycleResults, setCycleResults] = useState([]);
  const [cycleToCompare, setCycleToCompare] = useState(null);
  const [cycleToCompareResults, setCycleToCompareResults] = useState([]);
  const [review, setReview] = useState(null);
  const [reviewAnswers, setReviewAnswers] = useState([]);
  const [compareReviewAnswers, setCompareReviewAnswers] = useState([]);
  const [visibleRatings, setVisibleRatings] = useState({
    [SELF_REVIEW_STEP]: true,
    [TOP_DOWN_STEP]: true,
    [DOWN_UP_STEP]: true,
  });

  const cycleOptions = useMemo(
    () => cycles.filter(currentCycle => currentCycle.id !== cycleToCompare?.id),
    [cycles, cycleToCompare]
  );
  const cycleCompareOptions = useMemo(
    () => cycles.filter(currentCycle => currentCycle.id !== cycle?.id),
    [cycles, cycle]
  );
  const currentAttributes = useMemo(
    () =>
      prepareCycleAttributes(
        translations.steps,
        cycleResults,
        cycleToCompareResults
      ),
    [translations, cycleResults, cycleToCompareResults]
  );
  const attributesToCompare = useMemo(
    () =>
      prepareCycleAttributesForCompare(
        translations.steps,
        cycleResults,
        cycleToCompareResults
      ),
    [translations, cycleResults, cycleToCompareResults]
  );
  const answersPreviewTitle = useMemo(() => {
    if (review) {
      if (review.stepType === CYCLE_STEP_TYPES.SELF_REVIEW_STEP) {
        return translations.steps.self;
      }

      if (review.stepType === CYCLE_STEP_TYPES.TOP_DOWN_STEP) {
        return translations.steps.topDown;
      }

      if (review.stepType === CYCLE_STEP_TYPES.DOWN_UP_STEP) {
        return translations.steps.downUp;
      }
    }

    return '';
  }, [translations, review]);

  const getReviewCycle = useCallback(
    cycleId => http.get(api_user_review_cycle(id, cycleId)),
    [id]
  );

  const getReviewAnswers = (cycleId, attributeId, stepType) =>
    http.get(api_user_review_cycle_open_ended_answers(id, cycleId), {
      params: {
        [PARAMS.ATTRIBUTE]: attributeId,
        [PARAMS.CYCLE_STEP_TYPE]: stepType,
      },
    });

  const onChangeCycle = async selectedCycle => {
    const { data } = await getReviewCycle(selectedCycle.id);
    setCycle(selectedCycle);
    setCycleResults(data);
  };

  const onChangeCycleToCompare = async selectedCycle => {
    const { data } = await getReviewCycle(selectedCycle.id);
    setCycleToCompare(selectedCycle);
    setCycleToCompareResults(data);
  };

  const onChangeRatingVisibility = rating => () =>
    setVisibleRatings(prevRatings => ({
      ...prevRatings,
      [rating]: !prevRatings[rating],
    }));

  const onOpenAnswersPreview = async (attribute, stepType) => {
    const hasReviewAnswers = getItemById(cycleResults, attribute.id)?.replies?.[
      stepType
    ];
    const hasCompareAnswers = getItemById(cycleToCompareResults, attribute.id)
      ?.replies?.[stepType];
    const [reviewAnswersData, compareReviewAnswersData] = await Promise.all([
      ...(hasReviewAnswers
        ? [getReviewAnswers(cycle.id, attribute.id, stepType)]
        : []),
      ...(hasCompareAnswers
        ? [getReviewAnswers(cycleToCompare.id, attribute.id, stepType)]
        : []),
    ]);

    setReview({ ...attribute, stepType });
    setReviewAnswers(
      hasReviewAnswers
        ? prepareEmployeeReviewAnswers(reviewAnswersData?.data || null)
        : []
    );
    setCompareReviewAnswers(
      hasReviewAnswers
        ? prepareEmployeeReviewAnswers(compareReviewAnswersData?.data || null)
        : prepareEmployeeReviewAnswers(reviewAnswersData?.data || null)
    );
    setIsAnswersPreviewOpened(true);
  };
  const onCloseAnswersPreview = () => {
    setIsAnswersPreviewOpened(false);
    setReview(null);
    setReviewAnswers([]);
    setCompareReviewAnswers([]);
  };

  const getInitialData = useCallback(async () => {
    try {
      const { data: allCycles } = await http.get(api_user_review_cycles(id), {
        params: { [PARAMS.STATUS]: CYCLE_STATUSES.DONE },
      });
      const currentCycle = state?.cycleId
        ? getItemById(allCycles, +state.cycleId)
        : allCycles?.[0];

      if (!isArrayEmpty(allCycles) && currentCycle) {
        const userCycleResults = await getReviewCycle(currentCycle.id);
        setCycleResults(userCycleResults.data);
      }

      if (currentCycle) {
        setCycle(currentCycle);
      }

      setCycles(allCycles);
    } finally {
      setIsInitialLoad(false);
    }
  }, [id, state, getReviewCycle]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      location={location}
      userId={userId}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.header}>
            <Typography variant="body2">{translations.description}</Typography>
          </div>
          {!isArrayEmpty(cycles) && (
            <div>
              <SelectField
                className={classes.cycleSelect}
                label={translations.cycleLabel}
                value={cycle}
                options={cycleOptions}
                parser={{ value: 'id', label: 'title' }}
                onChange={onChangeCycle}
                shouldReturnOption
                isSearchDisabled
              />
              <div
                className={classNames(
                  classes.section,
                  classes.analyticsSection
                )}
              >
                <SectionTitle
                  className={classes.analyticsSectionTitle}
                  variant="h2"
                  title={translations.analyticsSection}
                />
                <SelectField
                  className={classes.cycleSelect}
                  label={translations.compareWithLabel}
                  placeholder={translations.compareWithPlaceholder}
                  value={cycleToCompare}
                  options={cycleCompareOptions}
                  parser={{ value: 'id', label: 'title' }}
                  onChange={onChangeCycleToCompare}
                  shouldReturnOption
                  isSearchDisabled
                />
                <EmployeeCycleAttributes
                  translations={translations.cycleAttributes}
                  attributes={currentAttributes}
                  onPreviewAnswers={onOpenAnswersPreview}
                />
                <EmployeeCycleAttributes
                  translations={translations.cycleAttributes}
                  attributes={attributesToCompare}
                  onPreviewAnswers={onOpenAnswersPreview}
                />
              </div>
              <div
                className={classNames(classes.section, classes.ratingsSection)}
              >
                <SectionTitle
                  variant="h2"
                  title={translations.ratingsSection}
                />
                <div className={classes.actionsWrapper}>
                  <CustomButton
                    className={classNames(classes.actionButton, {
                      [classes.selectedAction]:
                        visibleRatings[SELF_REVIEW_STEP],
                    })}
                    type="rounded"
                    startIcon={
                      <SelfReviewIcon
                        className={classNames(classes.actionIcon, {
                          [classes.actionIconSelected]:
                            visibleRatings[SELF_REVIEW_STEP],
                        })}
                      />
                    }
                    onClick={onChangeRatingVisibility(SELF_REVIEW_STEP)}
                  >
                    {translations.steps.self}
                  </CustomButton>
                  <CustomButton
                    className={classNames(classes.actionButton, {
                      [classes.selectedAction]: visibleRatings[TOP_DOWN_STEP],
                    })}
                    type="rounded"
                    startIcon={
                      <TopDownReviewIcon
                        className={classNames(classes.actionIcon, {
                          [classes.actionIconSelected]:
                            visibleRatings[TOP_DOWN_STEP],
                        })}
                      />
                    }
                    onClick={onChangeRatingVisibility(TOP_DOWN_STEP)}
                  >
                    {translations.steps.topDown}
                  </CustomButton>
                  <CustomButton
                    className={classNames(classes.actionButton, {
                      [classes.selectedAction]: visibleRatings[DOWN_UP_STEP],
                    })}
                    type="rounded"
                    startIcon={
                      <DownUpReviewIcon
                        className={classNames(classes.actionIcon, {
                          [classes.actionIconSelected]:
                            visibleRatings[DOWN_UP_STEP],
                        })}
                      />
                    }
                    onClick={onChangeRatingVisibility(DOWN_UP_STEP)}
                  >
                    {translations.steps.downUp}
                  </CustomButton>
                </div>
              </div>
              <EmployeeCycleAttributeRatings
                translations={translations.cycleAttributeRatings}
                attributes={cycleResults}
                visibleRatings={visibleRatings}
              />
            </div>
          )}
          <NotificationCard
            title={translations.noResultsTitle}
            content={translations.noResultsMessage}
            shouldFade={isArrayEmpty(cycles)}
          />
          <EmployeeCycleReviewAnswersPreview
            translations={translations}
            title={answersPreviewTitle}
            isOpened={isAnswersPreviewOpened}
            cycle={cycle}
            cycleToCompare={cycleToCompare}
            review={review}
            reviewAnswers={reviewAnswers}
            compareReviewAnswers={compareReviewAnswers}
            onClose={onCloseAnswersPreview}
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleReviewCyclesPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default memo(PeopleReviewCyclesPage);
